/**
 * Zod Validation Schemas for Template Customization
 *
 * Provides runtime validation for template customization data structures
 * with size limits and security constraints.
 */
import { z } from 'zod';
// ============================================================================
// CONFIGURATION & LIMITS
// ============================================================================
/**
 * Maximum size for individual HTML snippets (10KB)
 */
const MAX_HTML_SIZE = 10 * 1024;
/**
 * Maximum number of HTML attributes per element
 */
const MAX_ATTRIBUTES = 50;
/**
 * Maximum total size for entire customization object (100KB)
 */
const MAX_TOTAL_SIZE = 100 * 1024;
/**
 * Maximum length for className strings
 */
const MAX_CLASSNAME_LENGTH = 500;
/**
 * Maximum length for description field
 */
const MAX_DESCRIPTION_LENGTH = 500;
// ============================================================================
// SCHEMAS
// ============================================================================
/**
 * Schema for slot wrapper configuration
 */
export const SlotWrapperSchema = z.object({
    before: z
        .string()
        .max(MAX_HTML_SIZE, `HTML snippet exceeds maximum size of ${MAX_HTML_SIZE} bytes`)
        .optional(),
    after: z
        .string()
        .max(MAX_HTML_SIZE, `HTML snippet exceeds maximum size of ${MAX_HTML_SIZE} bytes`)
        .optional(),
    className: z
        .string()
        .max(MAX_CLASSNAME_LENGTH, `Class string exceeds maximum length of ${MAX_CLASSNAME_LENGTH} characters`)
        .optional(),
    wrapInner: z.boolean().optional(),
    attributes: z
        .record(z.string())
        .refine((obj) => Object.keys(obj).length <= MAX_ATTRIBUTES, {
        message: `Maximum ${MAX_ATTRIBUTES} attributes allowed per element`,
    })
        .optional(),
});
/**
 * Schema for section wrapper configuration
 */
export const SectionWrapperSchema = z.object({
    before: z
        .string()
        .max(MAX_HTML_SIZE, `HTML snippet exceeds maximum size of ${MAX_HTML_SIZE} bytes`)
        .optional(),
    after: z
        .string()
        .max(MAX_HTML_SIZE, `HTML snippet exceeds maximum size of ${MAX_HTML_SIZE} bytes`)
        .optional(),
    className: z
        .string()
        .max(MAX_CLASSNAME_LENGTH, `Class string exceeds maximum length of ${MAX_CLASSNAME_LENGTH} characters`)
        .optional(),
    wrapInner: z.boolean().optional(),
    attributes: z
        .record(z.string())
        .refine((obj) => Object.keys(obj).length <= MAX_ATTRIBUTES, {
        message: `Maximum ${MAX_ATTRIBUTES} attributes allowed per element`,
    })
        .optional(),
});
/**
 * Schema for root wrapper configuration
 */
export const RootWrapperSchema = z.object({
    className: z
        .string()
        .max(MAX_CLASSNAME_LENGTH, `Class string exceeds maximum length of ${MAX_CLASSNAME_LENGTH} characters`)
        .optional(),
    attributes: z
        .record(z.string())
        .refine((obj) => Object.keys(obj).length <= MAX_ATTRIBUTES, {
        message: `Maximum ${MAX_ATTRIBUTES} attributes allowed`,
    })
        .optional(),
    style: z
        .record(z.string())
        .refine((obj) => Object.keys(obj).length <= MAX_ATTRIBUTES, {
        message: `Maximum ${MAX_ATTRIBUTES} style properties allowed`,
    })
        .optional(),
});
/**
 * Schema for customization metadata
 */
export const TemplateCustomizationMetadataSchema = z.object({
    version: z.number().int().positive().optional(),
    description: z
        .string()
        .max(MAX_DESCRIPTION_LENGTH, `Description exceeds maximum length of ${MAX_DESCRIPTION_LENGTH} characters`)
        .optional(),
    lastModified: z.coerce.date().optional(),
    modifiedBy: z.string().optional(),
});
/**
 * Schema for template customization
 *
 * Validates structure, field sizes, and total customization size
 */
export const TemplateCustomizationSchema = z
    .object({
    extends: z.string().min(1, 'Template ID is required'),
    rootWrapper: RootWrapperSchema.optional(),
    slotWrappers: z.record(SlotWrapperSchema).optional(),
    sectionWrappers: z.record(SectionWrapperSchema).optional(),
    additions: z
        .record(z.string().max(MAX_HTML_SIZE, `HTML snippet exceeds maximum size of ${MAX_HTML_SIZE} bytes`))
        .optional(),
    metadata: TemplateCustomizationMetadataSchema.optional(),
})
    .refine((data) => {
    // Check total serialized size
    const totalSize = JSON.stringify(data).length;
    return totalSize <= MAX_TOTAL_SIZE;
}, {
    message: `Total customization size exceeds ${MAX_TOTAL_SIZE} bytes`,
});
/**
 * Schema for map of template customizations
 */
export const TemplateCustomizationMapSchema = z.record(TemplateCustomizationSchema);
// ============================================================================
// EXPORTS
// ============================================================================
/**
 * Export configuration constants for use in services
 */
export const CUSTOMIZATION_LIMITS = {
    MAX_HTML_SIZE,
    MAX_ATTRIBUTES,
    MAX_TOTAL_SIZE,
    MAX_CLASSNAME_LENGTH,
    MAX_DESCRIPTION_LENGTH,
};
//# sourceMappingURL=templateCustomization.schema.js.map